import { PatientOrderMixedDetermination } from 'flow-schema';

import { type BaseEhrPatient, type BaseItem, type NullablePartial } from '../common.js';
import type { Address, Patient, PatientInsuranceInformation, Practice, User } from '../index.js';

export enum AuthorizationStatus {
  Unknown = 'UNKNOWN',
  New = 'NEW',
  ProcessingSubmission = 'PROCESSING_SUBMISSION',
  ProcessedApproved = 'PROCESSED_APPROVED',
  ProcessedException = 'PROCESSED_EXCEPTION',
  ProcessedMixedDetermination = 'PROCESSED_MIXED_DETERMINATION',
  ProcessedNotRequired = 'PROCESSED_NOT_REQUIRED',
  ProcessedPending = 'PROCESSED_PENDING',
  ProcessedRejected = 'PROCESSED_REJECTED',
  Approved = 'APPROVED',
  Exception = 'EXCEPTION',
  MixedDetermination = 'MIXED_DETERMINATION',
  NotRequired = 'NOT_REQUIRED',
  Pending = 'PENDING',
  Rejected = 'REJECTED',
  NewPending = 'NEW_PENDING',
  ProcessingStatusing = 'PROCESSING_STATUSING',
  StatusingPending = 'STATUSING_PENDING',
  ManualReviewRequired = 'MANUAL_REVIEW_REQUIRED',
}

export const PatientOrderAuthorizationStatus = {
  ...AuthorizationStatus,
  ...PatientOrderMixedDetermination,
};
export type PatientOrderAuthorizationStatus = AuthorizationStatus | PatientOrderMixedDetermination;

export enum Flags {
  AuthorizationNotRequired = 'AUTHORIZATION_NOT_REQUIRED',
}

export type RawPatientOrder = NullablePartial<
  Omit<
    BasePatientOrder,
    | 'ehrInformationId'
    | 'referredByEhrProviderId'
    | 'referredToEhrProviderId'
    | 'alternateReferredToEhrProviderId'
    | 'payorAuthorizationNumber'
    | 'deletedTimestamp'
  > & {
    // patient
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    phoneNumber?: string | null;
    cellPhoneNumber?: string | null;
    homePhoneNumber?: string | null;
    workPhoneNumber?: string | null;
    email?: string | null;
    patientZipcode?: string | null;
    // encounter
    encounterEhrId?: string;
    // patient insurance information
    planName?: string;
    secondaryPlanName?: string;
    memberId?: string;
    secondaryMemberId?: string;
    groupName?: string;
    secondaryGroupName?: string;
    groupNumber?: string;
    secondaryGroupNumber?: string;
    payor?: string;
    secondaryPayor?: string;
    ehrPlanId?: string;
    secondaryEhrPlanId?: string;
    relationshipToPolicyHolder?: string;
    policyHolderFirstName?: string;
    policyHolderLastName?: string;
    policyHolderSex?: string;
    policyHolderDob?: Date;
    secondaryPolicyHolderFirstName?: string;
    secondaryPolicyHolderLastName?: string;
    secondaryPolicyHolderSex?: string;
    secondaryPolicyHolderDob?: Date;
    // ehr (patient) information
    patientMRN: string;
    // ehr referred by provider information
    referredByProviderEhrId: string;
    referredByProviderPhoneNumber: string | null;
    referredByProviderFirstName: string | null;
    referredByProviderLastName: string | null;
    referredByProviderNpi: string | null;
    // ehr referred to provider information
    referredToProviderEhrId: string | null;
    referredToProviderPhoneNumber: string | null;
    referredToProviderFirstName: string | null;
    referredToProviderLastName: string | null;
    referredToProviderNpi: string | null;
    alternateReferredToProviderEhrId: string | null;
    // auth status
    authorizationStatus?: string;
    // return to clinic order
    followUpTime?: string;
    // diagnosis
    diagnoses?: string;
    // reason for exam
    reasonForExam?: string | null;
    // noa
    patientHospitalLocation?: string | null;
    patientType?: string | null;
    //request list status
    requestListStatus?: string | null;
  }
>;

export interface ImportPatientOrder extends RawPatientOrder {}

export const IMPORT_COLUMNS: (keyof ImportPatientOrder)[] = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'phoneNumber',
  'cellPhoneNumber',
  'homePhoneNumber',
  'workPhoneNumber',
  'email',
  'encounterEhrId',
  'planName',
  'secondaryPlanName',
  'memberId',
  'secondaryMemberId',
  'groupNumber',
  'secondaryGroupNumber',
  'payor',
  'secondaryPayor',
  'ehrPlanId',
  'secondaryEhrPlanId',
  'patientMRN',
  'ehrCreated',
  'ehrScheduled',
  'scheduleLimitDate',
  'referredByProviderEhrId',
  'referredByProviderPhoneNumber',
  'referredByProviderFirstName',
  'referredByProviderLastName',
  'referredByProviderNpi',
  'referredToProviderEhrId',
  'referredToProviderPhoneNumber',
  'referredToProviderFirstName',
  'referredToProviderLastName',
  'referredToProviderNpi',
  'alternateReferredToProviderEhrId',
  'ehrReferenceValue',
  'referredByDepartment',
  'referredToDepartment',
  'rawProcedures',
  'rawDiagnoses',
  'payerAuthorizationNumber',
  'registrationTime',
  'dischargeTime',
  'authorizationStatus',
  'specialty',
  'followUpTime',
  'followUpVisitType',
  'fin',
  'trackingNumber',
  'seriesAppointmentCount',
  'diagnoses',
  'reasonForExam',
  'patientZipcode',
  'patientHospitalLocation',
  'patientType',
  'reasonForVisit',
  'requestListStatus',
  'referralPriority',
];

export enum ReferralPriority {
  Routine = 'ROUTINE',
  Stat = 'STAT',
  Urgent = 'URGENT',
}

interface BasePatientOrder {
  alternateReferredToEhrProviderId: string | null;
  authEffectiveDate: Date | null;
  authExpirationDate: Date | null;
  authObtainedDate: Date | null;
  authRequestedDate: Date | null;
  customAttributes: Record<string, string> | null;
  deletedTimestamp: string | null;
  dischargeTime: Date | null;
  ehrCreated: Date | null;
  ehrInformationId: string;
  ehrOrderingEncounterReferenceValue: string | null;
  ehrReferenceValue: string | null;
  ehrScheduled: Date | null;
  ehrTimeSlotBookingId: number | null;
  externalDocumentPacketId: string | null;
  fileName: string | null;
  fin: string | null;
  followUpUnit: string | null;
  followUpValue: number | null;
  followUpVisitType: string | null;
  rawFollowUpVisitType: string | null;
  isTest: boolean | null;
  patientAddressId: string | null;
  patientHospitalLocation: string | null;
  patientType: string | null;
  patientZipcode: string | null;
  payer: string | null;
  payerAuthorizationNumber: string | null;
  rawDiagnoses: string | null;
  rawProcedures: string | null;
  reasonForExam: string | null;
  reasonForVisit: string | null;
  referralComment: string | null;
  referralCreated: Date | null;
  referralPriority: ReferralPriority | null;
  referredByDepartment: string | null;
  referredByEhrProviderId: string;
  referredToDepartment: string | null;
  referredToEhrProviderId: string | null;
  registrationTime: Date | null;
  requestedAppointmentTimePeriodEnd: Date | null;
  requestedAppointmentTimePeriodStart: Date | null;
  requestListStatus: string | null;
  scheduleLimitDate: Date | null;
  seriesAppointmentCount: number | null;
  specialty: string | null;
  status: string | null;
  trackingNumber: string | null;
  patientEventTraceNumber: string | null; // X12 Patient Event Loop TRN2 value
  submitterTransactionId: string | null; // X12 Header BHT3 value
  serviceReviewCategory: string | null; // X12 278N UM01
  serviceType: string | null; // X12 278N UM03
  serviceFacilityType: string | null; // X12 278N UM04
  levelOfService: string | null; // X12 278N UM06
  admissionType: string | null; // X12 278N CL101
  admissionSource: string | null; // X12 278N CL102
}

export interface PatientOrder extends BaseItem, BasePatientOrder {}

export type CreateValues = Partial<Omit<BasePatientOrder, 'created' | 'updated'>>;
export type UpdateValues = Partial<Omit<BasePatientOrder, 'created' | 'updated'>>;
export enum PayorPortal {
  Availity = 'AVAILITY',
  BcbsmsMedicalNecessity = 'BCBSMS_MEDICAL_NECESSITY',
  Carelon = 'CARELON',
  // CareCore = 'CARE_CORE',
  Cohere = 'COHERE',
  Evicore = 'EVICORE',
  // HealthHelp = 'HEALTH_HELP',
  MedSolutions = 'MEDSOLUTIONS',
  // MHK = 'MHK',
  RadMD = 'RADMD',
  UnitedHealthcare = 'UNITED_HEALTHCARE',
  Fax = 'FAX', // temporary for NOA
  Ehr = 'EHR', // for issues arising outside of payor portals
}

export interface Diagnosis {
  description: string;
  icd10Codes: string[] | null;
}
export interface Procedure {
  description: string;
  cptCode: string | null;
  ehrReferenceValue: string | null;
}

export enum OrderStatus {
  Canceled = 'Canceled',
  Completed = 'Completed',
  Pending = 'Pending',
  ReadyToSchedule = 'ReadyToSchedule',
  Scheduled = 'Scheduled',
}

export enum SubmissionStatus {
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
  Exception = 'EXCEPTION',
  NotRequired = 'NOT_REQUIRED',
  Pending = 'PENDING',
  Unknown = 'UNKNOWN',
}

export enum SubmissionMethod {
  Analyst = 'ANALYST',
  API = 'API',
  HumanTask = 'HUMAN_TASK',
  RPA = 'RPA',
}

interface BaseAuthorizationSubmission {
  patientOrderId: string;
  payorPortal: PayorPortal | null;
  requestedDate: Date | null;
  obtainedDate: Date | null;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  trackingNumber: string | null;
  authorizationNumber: string | null;
  notRequiredReason: string | null;
  notRequiredComment: string | null;
  exceptionReason: string | null;
  exceptionComment: string | null;
  rejectedReason: string | null;
  rejectedComment: string | null;
  pendingReason: string | null;
  pendingComment: string | null;
}

interface BaseAuthorizationSubmissionStatus {
  authorizationSubmissionId: string;
  verificationGsPath: string | null;
  status: SubmissionStatus;
  rawStatus: string;
  isCurrent: boolean;
}

export type AuthorizationSubmissionStatusPopulated = Pick<
  BaseAuthorizationSubmissionStatus,
  'status' | 'rawStatus' | 'verificationGsPath'
>;

export type AuthorizationSubmissionPopulated = BaseItem &
  BaseAuthorizationSubmission &
  AuthorizationSubmissionStatusPopulated & {
    procedures: Procedure[] | null;
  };

export interface PatientOrderPopulated extends PatientOrder, BaseEhrPatient {
  npi: string | null;
  authorizationStatus: AuthorizationStatus | null;
  authorizationSubmissions: AuthorizationSubmissionPopulated[] | null;
  authorizationNote: string | null;
  authorizationNotRequiredReason: NotRequiredReason | null;
  authorizationNotRequiredComment: string | null;
  authorizationExceptionReason: ExceptionReason | null;
  authorizationExceptionComment: string | null;
  authorizationRejectedReason: RejectedReason | null;
  authorizationRejectedComment: string | null;
  diagnoses: Diagnosis[];
  procedures: Procedure[];
  verificationGsPath: string | null; // DEPRECATED
}

export enum ExceptionReason {
  CaseNotFound = 'CASE_NOT_FOUND',
  ClinicalQuestionsPresent = 'CLINICAL_QUESTIONS_PRESENT',
  DiagnosisNotFound = 'DIAGNOSIS_NOT_FOUND',
  DuplicateRequest = 'DUPLICATE_REQUEST',
  FacilityNotFound = 'FACILITY_NOT_FOUND',
  HealthPlanAddressNotFound = 'HEALTH_PLAN_ADDRESS_NOT_FOUND',
  InvalidTrackingNumber = 'INVALID_TRACKING_NUMBER',
  OtherEhrError = 'OTHER_EHR_ERROR',
  OtherPortalError = 'OTHER_PORTAL_ERROR',
  PatientNotFound = 'PATIENT_NOT_FOUND',
  PayorNotSupported = 'PAYOR_NOT_SUPPORTED',
  ProcedureNotFound = 'PROCEDURE_NOT_FOUND',
  ProviderNotFound = 'PROVIDER_NOT_FOUND',
  ServiceNotInScope = 'SERVICE_NOT_IN_SCOPE',
  SubmissionInformationMissing = 'SUBMISSION_INFORMATION_MISSING',
  TaxIdNumberNotAvailable = 'TAX_ID_NUMBER_NOT_AVAILABLE',
}

export enum RejectedReason {
  EligibilityIssue = 'Eligibility Issue',
  NoDiagnosis = 'No Diagnosis',
  NoProcedure = 'No Procedure',
  LackOfDocumentation = 'Lack of documentation',
  PayerPortalIssues = 'Payer portal issues',
  PeerToPeerReview = 'Peer to Peer review',
  IncompleteDocumentation = 'Incomplete documentation',
  Scheduled1DayBeforeProcedure = 'Scheduled 1 day before procedure',
  Scheduled2DaysBeforeProcedure = 'Scheduled 2 days before procedure',
  TimeWindowTooSmall = 'Time window too small',
  CancelledAppointments = 'Cancelled appointments',
  RescheduledAppointments = 'Rescheduled appoints',
  OutOfNetwork = 'Out of network',
  DuplicateRequest = 'Duplicate request',
  Other = 'Other',
}

export enum NotRequiredReason {
  AuthNotRequired = 'AUTHORIZATION_NOT_REQUIRED',
  ExistingAuthObtained = 'EXISTING_AUTHORIZATION_OBTAINED',
  Other = 'OTHER',
}

export enum PendingReason {
  ClinicalQuestionsPresent = 'CLINICAL_QUESTIONS_PRESENT',
  StatusingNeeded = 'STATUSING_NEEDED',
}

export type UpdateStatus =
  | {
      status: AuthorizationStatus.Approved | AuthorizationStatus.ProcessedApproved;
      verificationGsPath: string;
      payorPortal: PayorPortal;
      payerAuthorizationNumber: string;
      effectiveDate: string;
      expirationDate: string;
      trackingNumber?: string;
      submissionMethod?: SubmissionMethod;
    }
  | {
      status: AuthorizationStatus.Exception | AuthorizationStatus.ProcessedException;
      exceptionReason: ExceptionReason;
      exceptionComment: string;
      trackingNumber?: string;
      verificationGsPath?: string;
      payorPortal?: PayorPortal;
      submissionMethod?: SubmissionMethod;
    }
  | {
      status: AuthorizationStatus.NotRequired | AuthorizationStatus.ProcessedNotRequired;
      verificationGsPath: string;
      payorPortal: PayorPortal;
      notRequiredReason: NotRequiredReason;
      notRequiredComment?: string;
      trackingNumber?: string;
      submissionMethod?: SubmissionMethod;
    }
  | {
      status: AuthorizationStatus.Pending | AuthorizationStatus.ProcessedPending;
      verificationGsPath: string;
      payorPortal: PayorPortal;
      trackingNumber: string;
      pendingReason?: PendingReason;
      pendingComment?: string;
      submissionMethod?: SubmissionMethod;
    }
  | {
      status: AuthorizationStatus.Rejected | AuthorizationStatus.ProcessedRejected;
      verificationGsPath: string;
      payorPortal: PayorPortal;
      rejectedReason: RejectedReason;
      rejectedComment?: string;
      requestedDate?: string;
      trackingNumber?: string;
      submissionMethod?: SubmissionMethod;
    };

export interface SubmissionJobParams {
  callbackUrl: string;
  practiceId: string;
  patientOrderId: string;
  patientOrderEhrScheduled: Date | null;
  fin: string | null;
  ehrReferenceValue: string | null;
  patientEhrId: string;
  ehrInformationId: string;
  providerUserId: string | null;
  encounterEhrReferenceValue: string | null;
  encounterId: string | null;
  encounterStartTime: Date | null;
  encounterEndPressedTimestamp: string | null;
  // to filter which payor portal to access
  payor: string | null;
  payorPlan: string | null;
  specialty: string | null;
  // to navigate to patient
  patient: Pick<Patient.Patient, 'firstName' | 'lastName' | 'dateOfBirth'>;
  insurance: Pick<PatientInsuranceInformation.BaseInsuranceInformation, 'memberId'> | null;
  submission: {
    cptCode: string;
    icd10Codes: string[];
    procedure: string | null;
    procedures: Procedure[];
    diagnoses:
      | {
          description: string;
          icd10Codes?: string[] | null | undefined;
        }[]
      | null;
    reasonForExam: string | null;
    referredByProvider: Pick<User.User, 'firstName' | 'lastName' | 'npi' | 'phoneNumber'>;
    practice: Pick<Practice.Practice, 'taxId' | 'phoneNumber'> & {
      address: NonNullable<Omit<Address.Address, 'unitNumber'>>;
    };
    serviceDate: Date;
    referredByDepartment: string | null;
    referredToDepartment: string | null;
    patientPhoneNumber: string | null;
  };
}
